/*!
 *  @preserve
 *  
 *  @module      iframe-resizer/react 5.3.1 (esm) - 2024-09-12
 *
 *  @license     GPL-3.0 for non-commercial use only.
 *               For commercial use, you must purchase a license from
 *               https://iframe-resizer.com/pricing
 * 
 *  @description Keep same and cross domain iFrames sized to their content 
 *
 *  @author      David J. Bradshaw <info@iframe-resizer.com>
 * 
 *  @see         {@link https://iframe-resizer.com}
 * 
 *  @copyright  (c) 2013 - 2024, David J. Bradshaw. All rights reserved.
 */


import _extends from '@babel/runtime/helpers/extends';
import connectResizer from '@iframe-resizer/core';
import React, { useRef, useEffect, useImperativeHandle } from 'react';

const filterIframeAttribs = props => {
  const {
    license,
    bodyBackground,
    bodyMargin,
    bodyPadding,
    checkOrigin,
    direction,
    inPageLinks,
    log,
    offset,
    offsetHeight,
    offsetWidth,
    scrolling,
    tolerance,
    warningTimeout,
    waitForLoad,
    onClosed,
    onReady,
    onMessage,
    onResized,
    ...iframeProps
  } = props;
  return iframeProps;
};

// TODO: Add support for React.forwardRef() in next major version (Breaking change)
function IframeResizer(props) {
  // eslint-disable-next-line react/prop-types
  const {
    forwardRef,
    ...rest
  } = props;
  const filteredProps = filterIframeAttribs(rest);
  const iframeRef = useRef(null);
  const onClose = () => {
    // eslint-disable-next-line no-console
    console.warn(`[iframe-resizer/react][${iframeRef?.current?.id}] Close event ignored, to remove the iframe update your React component.`);
    return false;
  };

  // This hook is only run once, as once iframe-resizer is bound, it will
  // deal with changes to the element and does not need recalling
  useEffect(() => {
    const iframe = iframeRef.current;
    const resizer = connectResizer({
      ...rest,
      onClose
    })(iframe);
    return () => resizer?.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(forwardRef, () => ({
    getRef: () => iframeRef,
    getElement: () => iframeRef.current,
    resize: () => iframeRef.current.iframeResizer.resize(),
    moveToAnchor: anchor => iframeRef.current.iframeResizer.moveToAnchor(anchor),
    sendMessage: (message, targetOrigin) => {
      iframeRef.current.iframeResizer.sendMessage(message, targetOrigin);
    }
  }));

  // eslint-disable-next-line jsx-a11y/iframe-has-title
  return /*#__PURE__*/React.createElement("iframe", _extends({}, filteredProps, {
    ref: iframeRef
  }));
}

export { IframeResizer as default };
